// HALAL CALGARY - DOM ACCESS
//
// One chokepoint for reaching the document. Its own file, loaded first by
// every layout, because admin.js never loads main.js and articles.js runs
// before it. Top-level in a classic script, so later scripts see `dom` by
// name.
//
// Caching: dom.id() memoises (and dom.drop() forgets) - only for page chrome
// rendered once and never replaced. dom.all()/one() never cache: lists are
// rebuilt as people use the site, and a stale list silently writes to
// detached nodes.

// THE PAGE CONFIG, published as globals.
// The layouts put settings in <script type="application/json" id="hc-config">
// (data, so the page carries no inline JS); each key becomes window.__hc<Key>
// (siteName -> window.__hcSiteName). A block that fails to parse publishes
// nothing, and every reader has a default.
(function () {
  const holder = document.getElementById('hc-config');

  if (!holder) return;

  let config;

  try {
    config = JSON.parse(holder.textContent || '{}');
  } catch (err) {
    return;
  }

  Object.keys(config || {}).forEach((key) => {
    window['__hc' + key.charAt(0).toUpperCase() + key.slice(1)] = config[key];
  });
})();

const dom = (() => {
  const cache = new Map();

  /** Filled on the first dom.icons() call; see there. */
  let iconCache = null;

  return {
    /**
     * A stable, page-chrome element, looked up once and remembered. Misses
     * are cached too (as null) - these run in scroll and resize handlers.
     */
    id(id) {
      if (!cache.has(id)) cache.set(id, document.getElementById(id));

      return cache.get(id);
    },

    /**
     * An element by id, looked up fresh every time - for generated ids like
     * `place-card-<slug>` whose nodes are rebuilt. Not dom.one('#' + id): a
     * slug need not be a valid CSS identifier.
     */
    idLive(id) {
      return document.getElementById(id);
    },

    /** First match. Not cached: callers point these into changing DOM. */
    one(selector, root) {
      return (root || document).querySelector(selector);
    },

    /** All matches, as a real array so map/filter/find work without a spread. */
    all(selector, root) {
      return Array.from((root || document).querySelectorAll(selector));
    },

    /**
     * The inert <template id="hc-icons"> as a name => markup map, so toggled
     * icons are exactly the server's markup. Read once.
     */
    icons() {
      if (iconCache) return iconCache;

      const template = document.getElementById('hc-icons');

      iconCache = {};

      if (template && template.content) {
        this.all('[data-icon]', template.content).forEach((holder) => {
          iconCache[holder.getAttribute('data-icon')] = holder.innerHTML.trim();
        });
      }

      return iconCache;
    },

    /** Forget a cached id, for an element deliberately removed or replaced. */
    drop(id) {
      cache.delete(id);
    },

    /**
     * Replaces a container's children by PATCHING (morphdom), not rebuilding,
     * so unchanged nodes keep their identity: scroll position, focus, hover,
     * selection, loaded lazy images and browser-owned state all survive.
     * Rows are matched by nodeKey() below; without keys matching is
     * positional and an insert at the top makes every row look changed.
     *
     * THE ONE WAY THE PAGE IS WRITTEN: patch() for a container's children,
     * morph() for an element itself, text() for its text - all morphdom - and
     * insert()/remove() for a node that is new or gone, the two writes
     * morphdom has no verb for. No other script writes markup or moves nodes
     * by hand (tests/js/dom-writes.test.mjs).
     *
     * @param container  the element whose CHILDREN are replaced; it is never
     *                   itself touched.
     * @param source     an element holding the desired children (cheaper), or
     *                   a string of markup.
     * @param options    `keepAttributes(el)` - true for elements whose
     *                   aria-expanded/hidden/open are the visitor's state, not
     *                   the server's, and must survive the patch.
     */
    patch(container, source, options) {
      this.reconcile(container, this.asChildrenOf(container, source), true, options);
    },

    /**
     * Morphs an ELEMENT ITSELF into `source` - its tag's attributes and its
     * children - keeping the node (and its listeners) where morphdom can. For
     * a card or row redrawn from fresh markup; patch() when only the children
     * change.
     *
     * @param el       the live element.
     * @param source   the desired element, or markup holding exactly one.
     * @param options  as patch().
     */
    morph(el, source, options) {
      const target = typeof source === 'string'
        ? this.build(source, el.parentNode && el.parentNode.nodeType === 1 ? el.parentNode.tagName : 'div').firstElementChild
        : source;

      if (!target) return;

      this.reconcile(el, target, false, options);
    },

    /**
     * Sets an element's text through morphdom: its children become one text
     * node (or none, for '' / null). Equal text writes nothing, so a ticking
     * counter costs a comparison, not a mutation.
     */
    text(el, value) {
      const text = value == null ? '' : String(value);
      const only = el.childNodes.length === 1 ? el.firstChild : null;

      if (only ? only.nodeType === 3 && only.nodeValue === text : text === '' && el.childNodes.length === 0) return;

      const target = el.cloneNode(false);

      if (text !== '') target.appendChild(document.createTextNode(text));

      this.reconcile(el, target, true);
    },

    /**
     * Adds NEW nodes - an insert is not a difference, so there is nothing for
     * morphdom to reconcile. `source` is a node (or fragment) or markup, placed
     * before `before` (the end when omitted). Returns the first element added.
     */
    insert(parent, source, before) {
      const nodes = typeof source === 'string'
        ? Array.from(this.build(source, parent.tagName).childNodes)
        : [source];
      const ref = before || null;
      let first = null;

      nodes.forEach((node) => {
        const added = node.nodeType === 11 ? Array.from(node.childNodes) : [node];

        parent.insertBefore(node, ref);
        if (!first) first = added.find((n) => n.nodeType === 1) || null;
      });

      return first;
    },

    /** Takes a node out of the page - the other write morphdom has no verb for. */
    remove(el) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    },

    /**
     * Markup as a DETACHED, INERT tree: an element of `tag` (a div by default)
     * holding the parsed nodes. Parsed by DOMParser and left in its document,
     * which has no browsing context - no script runs, no image loads, no
     * handler fires - until the nodes are placed in the page. Rows parse inside
     * a <table>, since the parser drops a bare <tr>.
     */
    build(markup, tag) {
      const name = String(tag || 'div').toUpperCase();
      const rowContext = /^(TABLE|TBODY|THEAD|TFOOT)$/.test(name);
      const doc = new DOMParser().parseFromString(
        rowContext ? '<table><tbody>' + markup + '</tbody></table>' : markup,
        'text/html'
      );
      const from = rowContext ? doc.querySelector('tbody') : doc.body;
      const target = doc.createElement(name.toLowerCase());

      if (from) {
        while (from.firstChild) target.appendChild(from.firstChild);
      }

      return target;
    },

    /**
     * morphdom, with the page's rules: rows matched by nodeKey(), a chart's
     * canvas left to its chart, the visitor's disclosure state kept where
     * asked, and focus held on a node that a reorder moves.
     */
    reconcile(from, to, childrenOnly, options) {
      const opts = options || {};

      // KEEPING A ROW MOVED BY THE PATCH FOCUSED: a reorder moves the row,
      // and the browser blurs a node that leaves the document even briefly.
      // Restored below with preventScroll, since plain focus() scrolls every
      // ancestor, the document included.
      const focused = from.contains(document.activeElement)
        ? document.activeElement
        : null;

      window.morphdom(from, to, {
        childrenOnly,
        getNodeKey: this.nodeKey,
        onBeforeElUpdated(fromEl, toEl) {
          /* A CANVAS BELONGS TO WHATEVER DREW INTO IT, not to the server.
             Matching the server's bare <canvas> would strip the width, height
             and style Chart.js set, shrinking it to the 300x150 default (and
             pushing a donut's legend out of view). New canvases are still
             added and removed ones removed; only update-in-place is skipped. */
          if (fromEl.tagName === 'CANVAS') return false;

          if (opts.keepAttributes && opts.keepAttributes(fromEl)) {
            /* The visitor's state, not the server's - e.g. a <details> that
               ships closed must not snap shut on every refresh. */
            ['aria-expanded', 'hidden', 'open'].forEach((name) => {
              if (fromEl.hasAttribute(name)) {
                toEl.setAttribute(name, fromEl.getAttribute(name));
              } else {
                toEl.removeAttribute(name);
              }
            });
          }

          return true;
        },
      });

      // Only if it survived the patch and nothing else has taken focus.
      if (focused && focused !== document.activeElement && from.contains(focused)) {
        focused.focus({ preventScroll: true });
      }
    },

    /**
     * WHICH NODE IS WHICH, ACROSS A REDRAW: data-key, then data-expand, then
     * id. The source attribute is part of the key because a summary row's
     * data-expand="log-42" equals its detail row's id="log-42"; bare values
     * would collide and write one row's cells into the other.
     *
     * Read with getAttribute, never node.dataset: morphdom calls this for every
     * node of both trees (about 47k calls when the boot trims the server's
     * cards), and dataset makes the browser build a DOMStringMap per element.
     */
    nodeKey(node) {
      if (node.nodeType !== 1) return null;

      const key = node.getAttribute('data-key');

      if (key) return 'k:' + key;

      const expand = node.getAttribute('data-expand');

      if (expand) return 'x:' + expand;
      if (node.id) return 'i:' + node.id;

      return null;
    },

    /**
     * `source` as a detached element of the container's own tag: a node is
     * used as it is, markup is parsed by build() (inert until placed).
     */
    asChildrenOf(container, source) {
      return typeof source === 'string' ? this.build(source, container.tagName) : source;
    },
  };
})();

/* Tooltips live in main.js; the admin loads only dom.js + admin.js, so it
   gets native `title` boxes. */
